// Shared derive-on-read attribute logic for formed gutter PARTS — Downspouts and
// Elbows. Both pull Style / Connector Size / Material / Color out of a SKU's name
// + loose `size`/`color` fields with IDENTICAL rules, so the logic lives here
// once. (Downspouts add Length; Elbows add Type + Angle — those stay in their own
// files.) Tuned against the live dev `Downspouts` + `Elbows` categories (2026-06-17).
//
// JOINT CONTRACT: this module is consumed by BOTH downspout-attrs and elbow-attrs.
// Any change here must keep BOTH unit suites green. (Gutter coil deliberately keeps
// its OWN color/material lists — its bare-"White"→High-Gloss collapse is wrong for
// these parts, which keep the SP whites DISTINCT.)

import { inchesValue, titleCase, firstMatch } from './attr-parse-utils.js';

// Keyword-only styles (longest-first). Rectangular parts carry no style token →
// Style stays null (user fills in later).
const STYLES: Array<[RegExp, string]> = [
  [/k-?\s*style/i, 'K-Style'],
  [/half[-\s]?round/i, 'Half Round'], // BEFORE Round (longest-first) so "HALF ROUND" wins
  [/\bround\b/i, 'Round'],
  [/\bsmooth\b/i, 'Smooth'],
];

// Material — no bare "AL" (collides w/ INSTALL). Most parts are aluminum → default.
// ORDER MATTERS (firstMatch wins). Steel/Galvalume are checked BEFORE Copper so a
// steel "Copper Penny" part resolves to Steel, not Copper; Copper is word-boundaried
// (\bcopper\b) so the finish COLOR "Coppertone" does NOT mis-read as copper material.
// GALVANIZED→Steel (vendor convention) and GALVALUME are real materials (both also
// appear on the COLOR axis — the SP catalog carries them on both). Mirrors the coil
// parser's MATERIALS discipline (gutter-coil-attrs.ts).
const MATERIALS: Array<[RegExp, string]> = [
  [/alumin/i, 'Aluminum'],
  [/\bALUM\b/i, 'Aluminum'],
  [/galvalume/i, 'Galvalume'],
  [/\bGLUM\b/i, 'Galvalume'],
  [/galvaniz/i, 'Steel'],
  [/steel/i, 'Steel'],
  [/\bSTL\b/i, 'Steel'],
  [/\bcopper\b/i, 'Copper'], // \b so "Coppertone"/"Copper Penny"(steel) don't false-match
  [/\bCU\b/i, 'Copper'],
];

// The full Service-Partner color palette (45 names) — kept DISTINCT (Bone / High
// Gloss / Low Gloss / Satin White never collapse to a single "White"). A column
// value of bare "White" stays "White" (no forced gloss).
const CANON_COLORS = [
  'Almond', 'Black', 'Blue', 'Bonderized', 'Bone White', 'Bronze', 'Brown',
  'Buckskin Brown', 'Charcoal Gray', 'Clay', 'Colonial Gray', 'Copper',
  'Copper Penny', 'Coppertone', 'Cream', 'Dark Bronze', 'Dark Gray', 'Deep Black',
  'Eggshell', 'Galvalume', 'Galvanized', 'Green', 'Harvest Brown',
  'High Gloss White', 'Ivory', 'Light Pecan', 'Linen', 'Low Gloss White',
  'Mansard Brown', 'Matte Black', 'Medium Bronze', 'Mill', 'Musket Brown',
  'Pebble Clay', 'Red', 'Royal Brown', 'Sandcastle', 'Satin White', 'Sierra Tan',
  'Slate Gray', 'Terratone', 'Tuxedo Gray', 'Weathered Bronze', 'Wicker',
  'Wood Beige',
];

// PATH (a): structured `color` COLUMN → exact whole-string (case-insensitive)
// canonical lookup. No match → trust the column verbatim (title-cased).
const EXACT_COLOR = new Map(CANON_COLORS.map((c) => [c.toLowerCase(), c]));

// PATH (b): free-text NAME scan → longest-first regex so a multiword name wins
// before its substring ("Musket Brown" before "Brown"). Aliases handle the SP
// abbreviations seen in real round/corrugated names ("HI GLOSS WHITE", "WHT").
const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const NAME_COLORS: Array<[RegExp, string]> = [
  [/hi(?:gh)?\s*gloss\s*white/i, 'High Gloss White'],
  ...[...CANON_COLORS]
    .sort((a, b) => b.length - a.length)
    .map((c): [RegExp, string] => [new RegExp(`\\b${escapeRe(c)}\\b`, 'i'), c]),
  [/\bwht\b/i, 'White'], // SP abbreviation; gloss unspecified → bare White
  [/\bwhite\b/i, 'White'], // last-resort bare white (qualified whites matched above)
];

/** Keyword-only Style: K-Style/Round/Smooth literally in the name, else null. */
export function partStyle(name: string): string | null {
  return firstMatch(STYLES, name);
}

/** Material — keyword in the name, defaulting to Aluminum (business reality). */
export function partMaterial(name: string): string | null {
  return firstMatch(MATERIALS, name) ?? 'Aluminum';
}

/** Hardware material: Brass / Stainless / galvanized→Steel by name, else the
 *  partMaterial default (Aluminum). For the HARDWARE tables (hanger, wedge-shank)
 *  — kept OUT of partMaterial itself, which feeds the 8 formed-part tables. */
export function hardwareMaterial(name: string): string | null {
  if (/brass/i.test(name)) return 'Brass';
  if (/stainless|\bSST\b/i.test(name)) return 'Stainless';
  if (/galvaniz|\bgalv\b/i.test(name)) return 'Steel';
  return partMaterial(name);
}

/** Connector / outlet size. Rectangular "NxM" (from the `size` column first, then
 *  the name) → `N"xM"`; else a single inch dimension (round) → `D"`. */
export function connectorSize(name: string, size: string | null): string | null {
  for (const src of [size ?? '', name]) {
    // Allow an inch-mark between the dims — SP writes `2"x3"` / `2" X 3"`, not just `2x3`.
    const m = src.match(/(\d+)\s*["”]?\s*[xX]\s*(\d+)/);
    if (m) return `${m[1]}"x${m[2]}"`;
  }
  for (const src of [size ?? '', name]) {
    const m = src.match(/(\d+(?:\.\d+)?)\s*(?:"|”|in\b|inch(?:es)?\b)/i);
    if (m) return `${m[1]}"`;
  }
  return null;
}

/** Color: (a) the structured column, canonicalized/verbatim; else (b) a NAME scan. */
export function resolveColor(name: string, colorCol: string | null): string | null {
  const c = colorCol?.trim();
  if (c) return EXACT_COLOR.get(c.toLowerCase()) ?? titleCase(c);
  return firstMatch(NAME_COLORS, name);
}

/** Connector size sorts by its first dimension ("2"x3"" → 2, "3"" → 3). */
export function connectorSizeSortKey(v: string | null | undefined): number | null {
  return inchesValue(v?.replace(/"/g, ''));
}

/** Gutter-profile size: the first inch-marked dimension from the `size` column
 *  then the name ('5"', '6"'); else null. (A bare "(80 DEG)" never matches — it
 *  needs a `"`.) Shared by Miter + End Cap. */
export function partSize(name: string, size: string | null): string | null {
  for (const src of [size ?? '', name]) {
    const m = src.match(/(\d+)\s*"/);
    if (m) return `${m[1]}"`;
  }
  return null;
}

/** Size sorts numerically: '6"' → 6. */
export function partSizeSortKey(v: string | null | undefined): number | null {
  const m = v?.match(/\d+/);
  return m ? Number(m[0]) : null;
}

/** First inch-MARKED dimension, fraction-aware: '2 3/8"' | '1/2"' | '1.5"' | '6"'
 *  (mixed / bare-fraction / decimal / whole). partSize matches only whole inches;
 *  this is the richer form used by fastener Length + tool Size. */
export function markedInch(name: string): string | null {
  const m = name.match(/((?:\d+\s+)?\d+\/\d+|\d+(?:\.\d+)?)\s*"/);
  return m ? `${m[1]}"` : null;
}
